import React, { useEffect } from 'react';
import { Row, Col, Form, InputGroup, Badge } from 'react-bootstrap';
import { IconUser } from '@tabler/icons-react';
import AgentSelector from '../AgentSelector';
import { FORM_CONSTANTS } from '../../constants/formConstants';

const Step1AgentPrice = ({ 
  formData, 
  register, 
  setValue, 
  errors, 
  saving,
  trigger // Adicionar trigger para revalidação
}) => {
  
  // Auto-preencher nome e função quando agente é selecionado
  useEffect(() => {
    if (formData.agent && typeof formData.agent === 'object') {
      // Preencher nome se ainda não foi preenchido ou se mudou o agente
      if (formData.agent.agent_name && (!formData.agent_name || formData.agent_name === '')) {
        setValue('agent_name', formData.agent.agent_name);
      }
      
      // Preencher função se ainda não foi preenchido ou se mudou o agente
      if (formData.agent.agent_job && (!formData.agent_job || formData.agent_job === '')) {
        setValue('agent_job', formData.agent.agent_job);
      }
    }
  }, [formData.agent, setValue, formData.agent_name, formData.agent_job]);
  return (
    <div className="mpf-step-content">
      <div className="mpf-step-header">
        <IconUser size={24} className="text-primary" />
        <h4>Agente & Precificação</h4>
        <p className="text-muted">Selecione o agente, defina preços e descontos</p>
      </div>
      
      {/* Agente Selection */}
      <Row className="mb-4">
        <Col md={12}>
          <AgentSelector
            value={formData.agent}
            onChange={async (selectedAgent) => {
              console.log('📎 Agente selecionado:', selectedAgent);
              
              setValue("agent", selectedAgent);
              
              // Auto-preencher campos quando agente é selecionado
              if (selectedAgent && typeof selectedAgent === 'object') {
                console.log('📝 Preenchendo automaticamente:');
                
                if (selectedAgent.agent_name) {
                  console.log('  - Nome:', selectedAgent.agent_name);
                  setValue('agent_name', selectedAgent.agent_name);
                }
                if (selectedAgent.agent_job) {
                  console.log('  - Função:', selectedAgent.agent_job);
                  setValue('agent_job', selectedAgent.agent_job);
                }
                
                // Revalidar campos após preenchimento automático
                setTimeout(async () => {
                  console.log('🔄 Revalidando campos...');
                  const result = await trigger(['agent', 'agent_name', 'agent_job']);
                  console.log('✅ Resultado da revalidação:', result);
                }, 100);
              } else {
                console.log('🧹 Limpando campos');
                // Limpar campos se nenhum agente selecionado
                setValue('agent_name', '');
                setValue('agent_job', '');
                
                // Revalidar após limpeza
                setTimeout(async () => {
                  await trigger(['agent', 'agent_name', 'agent_job']);
                }, 100);
              }
            }}
            error={errors.agent?.message}
            disabled={saving}
            required
          />
        </Col>
      </Row>
      
      {/* Nome e Função do Agente - Lado a Lado */}
      <Row className="mb-4">
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Nome do Agente no Marketplace *</Form.Label>
            <Form.Control
              type="text"
              {...register("agent_name", {
                required: "Nome do agente é obrigatório",
                minLength: {
                  value: 3,
                  message: "Nome deve ter pelo menos 3 caracteres"
                },
                maxLength: {
                  value: 100,
                  message: "Nome deve ter no máximo 100 caracteres"
                }
              })}
              placeholder="Preenchido automaticamente..."
              disabled={saving}
              isInvalid={!!errors.agent_name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.agent_name?.message}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              <i className="las la-magic me-1" />
              Preenchido automaticamente. Você pode editar se necessário.
            </Form.Text>
          </Form.Group>
        </Col>
        
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Função/Cargo do Agente *</Form.Label>
            <Form.Control
              type="text"
              {...register("agent_job", {
                required: "Função do agente é obrigatória",
                minLength: {
                  value: 3,
                  message: "Função deve ter pelo menos 3 caracteres"
                },
                maxLength: {
                  value: 50,
                  message: "Função deve ter no máximo 50 caracteres"
                }
              })}
              placeholder="Preenchido automaticamente..."
              disabled={saving}
              isInvalid={!!errors.agent_job}
            />
            <Form.Control.Feedback type="invalid">
              {errors.agent_job?.message}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              <i className="las la-magic me-1" />
              Preenchido automaticamente. Você pode editar se necessário.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
      
      {/* Pricing Section */}
      <div className="border-top pt-4">
        <h5 className="mb-3">
          <i className="las la-tags text-primary me-2" />
          Configuração de Preços
        </h5>
        
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Preço de Venda (R$) *</Form.Label>
              <InputGroup>
                <InputGroup.Text>R$</InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={FORM_CONSTANTS.MIN_PRICE}
                  {...register("price")}
                  placeholder={FORM_CONSTANTS.MIN_PRICE.toFixed(2)}
                  disabled={saving}
                  isInvalid={!!errors.price}
                />
              </InputGroup>
              <Form.Control.Feedback type="invalid">
                {errors.price?.message}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Preço mínimo: R$ {FORM_CONSTANTS.MIN_PRICE},00
              </Form.Text>
            </Form.Group>
          </Col>
          
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Desconto (%)</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  {...register("pricing_info.discount_percentage")}
                  placeholder="0"
                  disabled={saving}
                />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                Desconto sobre o preço de venda
              </Form.Text>
            </Form.Group>
          </Col>
          
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Período de Teste (dias)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                {...register("pricing_info.trial_period_days")}
                placeholder="0"
                disabled={saving}
              />
              <Form.Text className="text-muted">
                Dias de teste gratuito
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
        
        {/* Price Preview */}
        <div className="border rounded p-3 bg-light">
          <h6 className="mb-2">
            <i className="las la-calculator text-muted me-2" />
            Prévia de Preços
          </h6>
          <div className="d-flex align-items-center gap-3">
            {formData.price && formData.pricing_info?.discount_percentage ? (
              <>
                <Badge bg="secondary" className="text-decoration-line-through">
                  R$ {parseFloat(formData.price).toFixed(2)}
                </Badge>
                <Badge bg="success" className="fs-6">
                  R$ {(parseFloat(formData.price) * (1 - parseFloat(formData.pricing_info.discount_percentage) / 100)).toFixed(2)}
                </Badge>
                <Badge bg="warning">
                  {formData.pricing_info.discount_percentage}% OFF
                </Badge>
              </>
            ) : (
              <Badge bg="primary" className="fs-6">
                R$ {formData.price || "0.00"}
              </Badge>
            )}
            
            {formData.pricing_info?.trial_period_days && (
              <Badge bg="info">
                {formData.pricing_info.trial_period_days} dias de teste
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step1AgentPrice;